import CircularLoader from 'components/CircularLoader';
import { DatePickerField } from 'components/forms/inputs/common/DatePickerField';
import { GroupBySelector } from 'components/GroupBySelector';
import InfoTooltip from 'components/InfoTooltip';
import FilterPersistenceToggle from 'components/tables/list-table/filter-pieces/FilterPersistenceToggle';
import { useFilterPersistence } from 'components/tables/list-table/useFilterPersistence';
import { ToggleGroup } from 'components/ui/toggle-group';
import { Tooltip } from 'components/ui/tooltip';
import { ViewSwitch, ViewSwitchItem } from 'components/ViewSwitch';
import { format, isValid, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useReserveBottomRow } from 'hooks/useReserveBottomRow';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useGetNewCasesSummaryQuery } from 'store/api/matters';
import { useGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { NewCasesGroupBy } from 'types/matters';
import { TeamType } from 'types/teams';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { NewCasesGroupRow } from './NewCasesGroupRow';
import { newCasesColumns } from './newCasesColumns';
import {
    ALL_OFFICES,
    matchingQuickPeriod,
    NewCasesFilters,
    newCasesParams,
    nextOfficeSelection,
    QUICK_PERIODS,
    QuickPeriod,
    quickPeriodSpan,
    readNewCasesFilters,
    writeNewCasesFilters,
} from './newCasesFilters';

const CLASSIFIER_CODES = ['MATTER_STATE_TYPE', 'REFERRAL_OTHER_SOURCE_TYPE'];
const GROUP_BY_OPTIONS = [NewCasesGroupBy.PRACTICE_GROUP, NewCasesGroupBy.SECTOR_GROUP] as const;
const ISO_DATE = 'yyyy-MM-dd';
const READ_TEAMS = ['READ.TEAMS'];
const READ_OFFICES = ['READ.OFFICES'];

const GROUP_BY_LABEL: Record<NewCasesGroupBy, string> = {
    [NewCasesGroupBy.PRACTICE_GROUP]: 'matters.new-cases.practice-groups',
    [NewCasesGroupBy.SECTOR_GROUP]: 'matters.new-cases.sector-groups',
};
const SUMMARY_TOOLTIP: Record<NewCasesGroupBy, string> = {
    [NewCasesGroupBy.PRACTICE_GROUP]: 'matters.new-cases.summary-tooltip-practice',
    [NewCasesGroupBy.SECTOR_GROUP]: 'matters.new-cases.summary-tooltip-sector',
};
const UNASSIGNED_TOOLTIP: Record<NewCasesGroupBy, string> = {
    [NewCasesGroupBy.PRACTICE_GROUP]: 'matters.new-cases.unassigned-tooltip-practice',
    [NewCasesGroupBy.SECTOR_GROUP]: 'matters.new-cases.unassigned-tooltip-sector',
};

/**
 * The case list's NEW tab: the cases opened in a period, counted per top-level practice or sector group. The
 * controls live in the URL, so a view can be bookmarked, and are remembered per the tab's own switch.
 */
export function NewCasesView(): JSX.Element {
    // Recomputed on every render, so a tab left open into a new week or month moves its relative period on the
    // next render, which a window refocus triggers by refetching.
    const todayIso = format(new Date(), ISO_DATE);
    const today = useMemo(() => parseISO(todayIso), [todayIso]);
    const dateFormat = useEffectiveDateFormat();
    const reserveBottomRow = useReserveBottomRow();
    const { getInitialSearchParams, isFilterPersistenceEnabled, toggleFilterPersistence, saveFilters } =
        useFilterPersistence({ canRememberFilters: true });
    const [searchParams, setSearchParams] = useSearchParams(getInitialSearchParams);
    const requested = useMemo(() => readNewCasesFilters(searchParams, today), [searchParams, today]);

    // Both probes answer 403 without their read privilege, so a user without it gets no such control.
    const canReadTeams = useHasPermissions(READ_TEAMS);
    const canReadOffices = useHasPermissions(READ_OFFICES);
    const { data: sectorGroupProbe, isLoading: isLoadingSectorGroups } = useGetMinTeamsListWithFiltersQuery(
        { page: 0, pageSize: 1, filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true;isActive==true` },
        { skip: !canReadTeams }
    );
    const hasSectorGroups = (sectorGroupProbe?.content.length ?? 0) > 0;
    const { data: officeData, isLoading: isLoadingOffices } = useGetMinOfficesWithFiltersQuery(
        { page: 0, pageSize: 2000, filters: 'isActive==true' },
        { skip: !canReadOffices }
    );
    const offices = useMemo(
        () => [...(officeData?.content ?? [])].sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)),
        [officeData]
    );

    // What the tenant cannot show falls back to the default: practice groups without a sector group, every
    // office without a choice of offices, and an office that is no longer active is dropped. Until the office
    // list has loaded, a failed request included, the requested offices are kept rather than widened to all.
    const filters = useMemo<NewCasesFilters>(
        () => ({
            ...requested,
            groupBy: hasSectorGroups ? requested.groupBy : NewCasesGroupBy.PRACTICE_GROUP,
            officeIds:
                canReadOffices && !officeData
                    ? requested.officeIds
                    : requested.officeIds.filter(
                          (id) => offices.length > 1 && offices.some((office) => office.id === id)
                      ),
        }),
        [requested, hasSectorGroups, offices, canReadOffices, officeData]
    );
    const params = useMemo(() => newCasesParams(filters), [filters]);
    const quickPeriod = matchingQuickPeriod(filters.from, filters.until, today);
    const from = useMemo(() => parseISO(filters.from), [filters.from]);
    const until = useMemo(() => parseISO(filters.until), [filters.until]);

    const { classifiers } = useClassifiers({ codes: CLASSIFIER_CODES });
    const columns = useMemo(() => newCasesColumns(classifiers, filters.groupBy), [classifiers, filters.groupBy]);
    const {
        currentData: summary,
        isFetching,
        isError,
    } = useGetNewCasesSummaryQuery(params, {
        skip: isLoadingSectorGroups || isLoadingOffices,
        refetchOnMountOrArgChange: true,
    });

    function applyFilters(next: NewCasesFilters) {
        const nextParams = writeNewCasesFilters(next, today);
        setSearchParams(nextParams);
        if (isFilterPersistenceEnabled) {
            saveFilters(nextParams);
        }
    }

    // An unfinished or out-of-range typed date is left in its field, flagged, and not applied.
    function applyPeriod(date: Date | null, bound: 'from' | 'until') {
        if (!date || !isValid(date)) {
            return;
        }
        const next = { ...filters, [bound]: format(date, ISO_DATE) };
        if (next.from <= next.until) {
            applyFilters(next);
        }
    }

    function rememberFilters(enabled: boolean) {
        if (enabled) {
            saveFilters(writeNewCasesFilters(filters, today));
        } else {
            toggleFilterPersistence(false);
        }
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                {/* Full basis: Office keeps a row of its own, so the Opened row always starts below it. */}
                {offices.length > 1 ? (
                    <div className="flex basis-full items-center gap-2">
                        <span className="text-fg-secondary">{t('matters.office')}</span>
                        <InfoTooltip text="matters.new-cases.office-tooltip" />
                        <ToggleGroup
                            variant="outline"
                            spacing={0}
                            multiple
                            aria-label={t('matters.office')}
                            value={filters.officeIds.length > 0 ? filters.officeIds.map(String) : [ALL_OFFICES]}
                            onValueChange={(pressed) =>
                                applyFilters({ ...filters, officeIds: nextOfficeSelection(pressed, filters.officeIds) })
                            }
                        >
                            <ViewSwitchItem value={ALL_OFFICES}>{t('all')}</ViewSwitchItem>
                            {offices.map((office) => (
                                <Tooltip key={office.id} content={office.name}>
                                    <ViewSwitchItem value={String(office.id)}>{office.abbreviation}</ViewSwitchItem>
                                </Tooltip>
                            ))}
                        </ToggleGroup>
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-fg-secondary">{t('matters.new-cases.opened')}</span>
                    <InfoTooltip text="matters.new-cases.opened-tooltip" />
                    <ViewSwitch<QuickPeriod | ''>
                        aria-label={t('matters.new-cases.opened')}
                        value={quickPeriod ?? ''}
                        onChange={(period) => period && applyFilters({ ...filters, ...quickPeriodSpan(period, today) })}
                    >
                        {QUICK_PERIODS.map((period) => (
                            <ViewSwitchItem key={period} value={period}>
                                {t(`matters.new-cases.${period}`)}
                            </ViewSwitchItem>
                        ))}
                    </ViewSwitch>
                    <span className="text-fg-secondary">{t('matters.new-cases.from')}</span>
                    <DatePickerField
                        className="w-40"
                        format={dateFormat}
                        value={from}
                        maxDate={until}
                        clearable={false}
                        onChange={(date) => applyPeriod(date, 'from')}
                    />
                    <span className="text-fg-secondary">{t('matters.new-cases.until')}</span>
                    <DatePickerField
                        className="w-40"
                        format={dateFormat}
                        value={until}
                        minDate={from}
                        clearable={false}
                        onChange={(date) => applyPeriod(date, 'until')}
                    />
                </div>
                <div className="ml-auto flex items-center gap-4">
                    <FilterPersistenceToggle enabled={isFilterPersistenceEnabled} onChange={rememberFilters} />
                    {hasSectorGroups ? (
                        <GroupBySelector
                            label={t('matters.new-cases.group-by')}
                            value={filters.groupBy}
                            options={GROUP_BY_OPTIONS}
                            optionLabel={(option) => t(GROUP_BY_LABEL[option])}
                            onChange={(groupBy) => applyFilters({ ...filters, groupBy })}
                        />
                    ) : null}
                </div>
            </div>
            {isError ? (
                <span className="text-fg-error">{t('matters.error-loading-matters-list')}</span>
            ) : !summary || isFetching ? (
                <div className="relative min-h-40">
                    <CircularLoader />
                </div>
            ) : (
                <>
                    <div className="text-body1 flex items-center gap-1 font-semibold">
                        <FormattedMessage
                            id="matters.new-cases.summary"
                            values={{ count: summary.caseCount, offers: summary.offerCount }}
                        />
                        <InfoTooltip text={SUMMARY_TOOLTIP[filters.groupBy]} />
                    </div>
                    {/* Keyed on the request, so every change of a control collapses every group. The last group's
                        row scrolls clear of an occupied bottom row; without one, an ordinary gap. */}
                    <div
                        key={JSON.stringify(params)}
                        className={cn('min-h-0 flex-1 overflow-y-auto', reserveBottomRow ? 'pb-24' : 'pb-6')}
                    >
                        {summary.groups.map((group) => (
                            <NewCasesGroupRow
                                key={group.id}
                                title={
                                    <FormattedMessage
                                        id="matters.new-cases.group-title"
                                        values={{ name: group.name, count: group.caseCount }}
                                    />
                                }
                                caseCount={group.caseCount}
                                offerCount={group.offerCount}
                                params={params}
                                groupId={group.id ?? undefined}
                                columns={columns}
                            />
                        ))}
                        <NewCasesGroupRow
                            title={
                                <>
                                    <FormattedMessage
                                        id="matters.new-cases.unassigned"
                                        values={{ count: summary.unassigned.caseCount }}
                                    />
                                    <InfoTooltip text={UNASSIGNED_TOOLTIP[filters.groupBy]} nested />
                                </>
                            }
                            caseCount={summary.unassigned.caseCount}
                            offerCount={summary.unassigned.offerCount}
                            params={params}
                            columns={columns}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
